import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

// ⭐ FULL DEV OVERRIDE LIST
export const DEV_OVERRIDE_EMAILS = [
  'nanaap20@yahoo.com',
  'nanaap20@gmail.com',
  'sena.appleh@gmail.com',
  'support@lumosye.com',
  'y.ansah@lumosye.com',
  's.ansah@lumosye.com',
  'reviewer@lumosyehealth.com',
];

export function SubscriptionProvider({ children }) {
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [isDev, setIsDev] = useState(false);   // ⭐ NEW: dev flag

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setSubscriptionTier('free');
      setTrialEndsAt(null);
      setIsTrialActive(false);
      setIsDev(false);
      return;
    }

    const userRef = doc(db, 'users', currentUser.uid);

    const unsubscribe = onSnapshot(userRef, async (snap) => {
      try {
        const email = currentUser.email?.toLowerCase() || '';
        const devMode = DEV_OVERRIDE_EMAILS.includes(email);

        // ⭐ NEW: Set dev mode flag globally
        setIsDev(devMode);

        // -------------------------------------------------------------
        //  🔥 DEV OVERRIDE — FULL UNLOCK + BADGE + PERMANENT PRO
        // -------------------------------------------------------------
        if (devMode) {
          console.log('🟣 DEV MODE ACTIVE → Full PRO access:', email);

          setSubscriptionTier('pro');
          setIsTrialActive(true);
          setTrialEndsAt(null);
          setLoading(false);

          // Ensure Firestore stays valid so security rules work
          await setDoc(
            userRef,
            {
              subscriptionTier: 'pro',
              subscriptionUpdatedAt: serverTimestamp(),
              isTrialActive: true
            },
            { merge: true }
          );

          return;
        }

        // -------------------------------------------------------------
        //  NORMAL USER LOGIC
        // -------------------------------------------------------------
        if (!snap.exists()) {
          const now = new Date();
          const end = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

          await setDoc(
            userRef,
            {
              subscriptionTier: 'free',
              trialStartedAt: serverTimestamp(),
              trialEndsAt: Timestamp.fromDate(end),
              isTrialActive: true,
              subscriptionUpdatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setSubscriptionTier('free');
          setTrialEndsAt(end);
          setIsTrialActive(true);
          setLoading(false);
          return;
        }

        // Existing user doc
        const data = snap.data() || {};
        const tier = data.subscriptionTier || 'free';
        let trialEnd = data.trialEndsAt instanceof Timestamp ? data.trialEndsAt.toDate() : null;

        // Start trial if missing
        if (!trialEnd) {
          const now = new Date();
          trialEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

          await updateDoc(userRef, {
            trialStartedAt: serverTimestamp(),
            trialEndsAt: Timestamp.fromDate(trialEnd),
            isTrialActive: true
          });
        }

        const now = new Date();
        const stillInTrial = trialEnd && now <= trialEnd;

        if (!!data.isTrialActive !== stillInTrial) {
          await updateDoc(userRef, { isTrialActive: stillInTrial });
        }

        setSubscriptionTier(tier);
        setTrialEndsAt(trialEnd);
        setIsTrialActive(stillInTrial);
        setLoading(false);
      } catch (err) {
        console.error('Subscription snapshot error:', err);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // -------------------------------------------------------
  //  📡 Global values exposed to all components
  // -------------------------------------------------------
  const value = {
    loading,
    subscriptionTier,
    isPro: subscriptionTier === 'pro',
    trialEndsAt,
    isTrialActive,

    // ⭐ NEW: Dev mode exposed to UI
    isDev,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used inside <SubscriptionProvider>');
  }
  return ctx;
}
